import type { HabConditionalRule, ItvPrefill, Location } from 'api';

type RuleLookup = (field: string) => HabConditionalRule | undefined;

// Coverage fields use frontend-specific values and their visibility is
// controlled by UI toggles, not Acturis conditional rules. Skip filtering
// these fields since their trigger fields (e.g., in_ground_swimming_pool)
// don't exist in the prefill data.
const SKIP_CONDITIONAL_FILTER_FIELDS = new Set([
  'swimming_pool_enabled',
  'swimming_pool_type',
  'swimming_pool_material',
  'swimming_pool_year_built',
  'swimming_pool_fenced',
  'solar_panel_enabled',
]);

const isFieldVisible = (
  merged: Record<string, unknown>,
  rule: HabConditionalRule,
): boolean => {
  return rule.rules.some((r) => {
    const triggerValue = merged[r.trigger_field];
    if (triggerValue === undefined || triggerValue === null) return false;
    const stringValue = String(triggerValue);
    return r.show_for_codes.includes(stringValue);
  });
};

/**
 * Drop prefill values for fields the form wouldn't show. Acturis gates many
 * habitational questions on the answer to another one, so a value that is
 * correct in isolation can still be wrong to write - a brick percentage means
 * nothing once the wall type is vinyl.
 */
export const filterPrefillByConditionalRules = (
  location: Location,
  prefill: ItvPrefill,
  getHabRule: RuleLookup,
): ItvPrefill => {
  let current: ItvPrefill = { ...prefill };

  // Dropping one field can hide another that follows it, so settle.
  for (;;) {
    const merged = { ...location, ...current } as Record<string, unknown>;
    const kept = Object.fromEntries(
      Object.entries(current).filter(([field]) => {
        if (SKIP_CONDITIONAL_FILTER_FIELDS.has(field)) return true;
        const rule = getHabRule(field);
        if (!rule) return true;
        return isFieldVisible(merged, rule);
      }),
    ) as ItvPrefill;

    if (Object.keys(kept).length === Object.keys(current).length) return kept;
    current = kept;
  }
};
